import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { ArrayFunctions } from '@campus/utils';
import { Subject } from 'rxjs';
import { InputBooleanAttribute, isTruthyInput } from '../core/empty-attribute';
import { NavigationRailItem, NavigationRailItemInterface } from './navigation-rail.interface';

@Component({
  standalone: false,
  selector: 'campus-navigation-core',
  template: '',
})
export class NavigationCoreComponent implements AfterViewInit, OnChanges, OnDestroy {
  //#region Viewchildren
  @ViewChild('itemsContainer', { static: false, read: ElementRef }) itemsContainer: ElementRef;
  @ViewChildren('item') viewItems: QueryList<NavigationRailItem>;
  @ViewChild('menuButton', { static: false, read: ElementRef }) menuButton: ElementRef;
  @ViewChild('backButton', { static: false, read: ElementRef }) backButton: ElementRef;
  @ViewChild('fabButton', { static: false, read: ElementRef }) fabButton: ElementRef;
  //#endregion

  //#region private properties

  private _railItems: HTMLElement[];
  private _focusedIndex: number;
  private _destroyed$ = new Subject<void>();
  //#endregion

  //#region Public properties

  @HostBinding('attr.tabindex') attrTabIndex = -1;
  @HostBinding('attr.role') role = 'navigation';

  @Input() fabIcon: string;
  @Input() fabLabel: string;
  @Input() backLabel = 'Terug';

  @Input() items: NavigationRailItemInterface[];
  @Input() align: 'top' | 'center' | 'bottom' = 'top';
  @Input() activeRouterLink: string[];

  @HostBinding('class.border-ie')
  @HostBinding('class.border-stroke-variant')
  @Input()
  divider: InputBooleanAttribute;

  @Input() useMenu: InputBooleanAttribute;
  @Input() useBack: InputBooleanAttribute;

  @HostBinding('attr.aria-label')
  @Input()
  ariaLabel: string;

  //#endregion

  //#region Outputs
  @Output() itemClick = new EventEmitter<NavigationRailItemInterface>();
  @Output() menuClick = new EventEmitter<void>();
  @Output() backClick = new EventEmitter<void>();
  @Output() fabClick = new EventEmitter<void>();
  //#endregion

  //#region public methods
  ngAfterViewInit(): void {
    this._onUpdatedItems();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.useMenu) {
      this.useMenu = isTruthyInput(this.useMenu);
    }
    if (changes.useBack) {
      this.useBack = isTruthyInput(this.useBack);
    }
    if (changes.divider) {
      this.divider = isTruthyInput(this.divider);
    }
    if (changes.items || changes.useMenu || changes.fabIcon || changes.useBack) {
      this._onUpdatedItems();
    }
    if (changes.activeRouterLink) {
      this.items = this.items.map((item) => {
        item.selected = ArrayFunctions.getArrayEquality(item.routerLink, this.activeRouterLink);
        return item;
      });

      this._onUpdatedItems();
    }
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  private _processUserInput(event: Event, item: NavigationRailItemInterface | 'menu' | 'back' | 'fab') {
    if (item === 'back') {
      this.backClick.emit();
    } else if (item === 'menu') {
      this.menuClick.emit();
    } else if (item === 'fab') {
      this.fabClick.emit();
    } else {
      this.itemClick.emit(item as NavigationRailItemInterface);
    }
  }

  onItemClick(event: MouseEvent, item: NavigationRailItemInterface | 'menu' | 'back' | 'fab') {
    this._processUserInput(event, item);
  }

  onActiveItemFocus(event: FocusEvent, item: NavigationRailItemInterface) {
    // Set the focused index back to the active item
    // after it was previously focused, unfocused, and then focused again.
    this._focusedIndex = this.getIndexOfActiveItem();
  }

  onItemKeyDown(event: KeyboardEvent, item: NavigationRailItemInterface | 'menu' | 'fab' | 'back') {
    let toIndex: number;
    const focusItem = (index: number) => {
      event.preventDefault();
      this._focusedIndex = index;
      this._railItems[index].focus();
    };

    switch (event.code) {
      case 'ArrowDown':
        toIndex = this._focusedIndex + 1;
        if (toIndex >= this._railItems.length) toIndex = 0;
        focusItem(toIndex);
        break;
      case 'ArrowUp':
        toIndex = this._focusedIndex - 1;
        if (toIndex < 0) toIndex = this._railItems.length - 1;
        focusItem(toIndex);
        break;
      case 'Home':
        focusItem(0);
        break;
      case 'End':
        focusItem(this._railItems.length - 1);
        break;
      case 'Enter':
        this._processUserInput(event, item);
        break;
    }
  }

  trackByLabel(index: number, item: NavigationRailItemInterface) {
    return item.label;
  }
  //#endregion

  //#region private methods

  private _onUpdatedItems() {
    this._railItems = this.getRailItems();
    this._focusedIndex = this.getIndexOfActiveItem();
    this.ariaLabel = this.getAriaLabel();
  }

  private getRailItems() {
    return [
      ...(this.useMenu ? [this.menuButton?.nativeElement] : []),
      ...(this.useBack ? [this.backButton?.nativeElement] : []),
      ...(this.fabIcon ? [this.fabButton?.nativeElement] : []),
      ...(this.itemsContainer?.nativeElement.querySelectorAll('a') || []),
    ].filter(Boolean);
  }
  private getIndexOfActiveItem() {
    return this._railItems.findIndex((item) => item.getAttribute('aria-selected') === 'true');
  }

  private getAriaLabel() {
    const count = this._railItems.length;

    return `${count} ${count === 1 ? 'onderdeel' : 'onderdelen'} in de navigatie.`;
  }
  //#endregion
}
